import { SystemMessage } from "@app/constants";
import * as Yup from "yup";

const isValidUrl = (value?: string) => {
  if (!value) return true;

  try {
    new URL(value);

    return true;
  } catch {
    return false;
  }
};

export const createCampaignValidationSchema = Yup.object().shape({
  // Campaign fields - only validate if user inputs something
  name: Yup.string().when("$hasCampaignData", {
    is: true,
    then: (schema) => schema.required(SystemMessage.INPUT_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  discount: Yup.number().when("$hasCampaignData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .min(0.01, "Discount lớn hơn không được nhỏ hơn 0.01")
      .max(100, "Discount phải nhỏ hơn 100"),
    otherwise: (schema) => schema.notRequired(),
  }),
  startDate: Yup.string().notRequired(),

  endDate: Yup.string().test(
    "date-validation",
    "Ngày kết thúc phải lớn hơn ngày bắt đầu",
    function (value) {
      const { startDate } = this.parent;

      // Only validate if both dates are provided
      if (!startDate || !value) return true;

      return new Date(value) > new Date(startDate);
    }
  ),

  // Optional campaign target validations
  birthday: Yup.boolean(),
  userType: Yup.array().of(Yup.mixed()),

  // Optional price range; if both provided (>0), enforce min <= max
  minPrice: Yup.number().when("$hasPriceRange", {
    is: true,
    then: (schema) => schema
      .min(0, "Giá thấp nhất không hợp lệ")
      .test(
        "min-lte-max",
        "Giá thấp nhất phải nhỏ hơn hoặc bằng giá cao nhất",
        function (minPrice) {
          const { maxPrice } = this.parent as { maxPrice?: number };

          if (minPrice && maxPrice) {
            return minPrice <= maxPrice;
          }

          return true;
        }
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  maxPrice: Yup.number().when("$hasPriceRange", {
    is: true,
    then: (schema) => schema.min(0, "Giá cao nhất không hợp lệ"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Notification fields - only validate if user inputs something
  notificationTitle: Yup.string().when("$hasNotificationData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .max(200, "Tiêu đề quá dài"),
    otherwise: (schema) => schema.notRequired(),
  }),
  notificationBody: Yup.string().when("$hasNotificationData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .max(2000, "Nội dung quá dài"),
    otherwise: (schema) => schema.notRequired(),
  }),
  notificationNote: Yup.string().max(500, "Ghi chú quá dài"),
  notificationTimeSend: Yup.string().when("$hasNotificationData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Định dạng thời gian phải là hh:mm"),
    otherwise: (schema) => schema.notRequired(),
  }),
  isRepeatNotification: Yup.boolean(),
  notificationRepeatDays: Yup.array()
    .of(
      Yup.mixed().test(
        "valid-day",
        "Ngày lặp lại không hợp lệ",
        (v) => {
          const day = typeof v === "string" ? v : (v && (v as any).value);

          return [
            "Thứ 2",
            "Thứ 3",
            "Thứ 4",
            "Thứ 5",
            "Thứ 6",
            "Thứ 7",
            "Chủ nhật",
          ].includes(day);
        }
      )
    )
    .when("isRepeatNotification", {
      is: true,
      then: (schema) => schema.min(1, "Chọn ít nhất 1 ngày lặp lại"),
      otherwise: (schema) => schema.notRequired(),
    }),

  // Popup banner fields - only validate if user inputs something
  content: Yup.string().when("$hasBannerData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .max(2000, "Nội dung banner quá dài"),
    otherwise: (schema) => schema.notRequired(),
  }),
  titleButton: Yup.string().when("$hasBannerData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .max(100, "Tiêu đề nút quá dài"),
    otherwise: (schema) => schema.notRequired(),
  }),
  actionButton: Yup.string().when("$hasBannerData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .test("is-url", "Button action phải là URL hợp lệ", (val) =>
        isValidUrl(val || "")
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  totalDisplayCount: Yup.number().when("$hasBannerData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .integer("Phải là số nguyên")
      .min(1, "Phải lớn hơn 0"),
    otherwise: (schema) => schema.notRequired(),
  }),
  totalDisplayTime: Yup.number().when("$hasBannerData", {
    is: true,
    then: (schema) => schema
      .required(SystemMessage.INPUT_REQUIRED)
      .integer("Phải là số nguyên")
      .min(1, "Phải lớn hơn 0"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // User filter fields
  userListenedToMinutes: Yup.string(),
  kasIsGreaterThan: Yup.string(),
  kasIsLessThan: Yup.string(),
  hasPurchasedBooks: Yup.array(),
  notHasPurchasedBooks: Yup.array(),
});
